import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

// A forecast period (e.g. "Aug-2026") covers the three following months.
// Every building row's month is derived from its Expected Date of Release, so
// the mapping lives here — mirrored server-side by CsdMonthly#forecast_window.

const parsePeriod = (period) => {
  if (!period) return null;
  const base = dayjs(period, 'MMM-YYYY', true);
  return base.isValid() ? base.startOf('month') : null;
};

export const getMonthOptions = (period) => {
  const base = parsePeriod(period);
  if (!base) return [];
  return [1, 2, 3].map((i) => ({
    index: i,
    label: base.add(i, 'month').format('MMM-YYYY'),
  }));
};

// Bounds for the date picker: 01 of M+1 through the last day of M+3.
export const getForecastWindow = (period) => {
  const base = parsePeriod(period);
  if (!base) return { minDate: null, maxDate: null };
  return {
    minDate: base.add(1, 'month').startOf('month').toDate(),
    maxDate: base.add(3, 'month').endOf('month').startOf('day').toDate(),
  };
};

// 1..3 for a date inside the forecast window, otherwise null.
export const monthIndexForDate = (period, date) => {
  const base = parsePeriod(period);
  if (!base || !date) return null;
  const d = dayjs(date, 'YYYY-MM-DD', true);
  if (!d.isValid()) return null;

  const index = (d.year() - base.year()) * 12 + (d.month() - base.month());
  return index >= 1 && index <= 3 ? index : null;
};

export const monthLabelFor = (period, monthIndex) => {
  const base = parsePeriod(period);
  if (!base || !monthIndex) return null;
  return base.add(monthIndex, 'month').format('MMM-YYYY');
};

const toRow = (building, month = {}) => ({
  id: month.id ?? null,
  building_id: building.building_id,
  building_name: building.building_name,
  expected_release_date: month.expected_release_date ? String(month.expected_release_date).slice(0, 10) : '',
  month_index: month.month_index ?? null,
  // The month the row is stored under on the server — Recalculate targets it,
  // so it must match the (possibly edited) month before recalculating.
  saved_month_index: month.id ? month.month_index : null,
  built_up_weight: month.built_up_weight || '',
  cold_formed_weight: month.cold_formed_weight || '',
  sheeting_weight: month.sheeting_weight || '',
  project_status_name: month.project_status_name || '',
  phase: month.phase || '',
  remark: month.remark || '',
  source_document: month.source_document || null,
});

// react-hook-form defaultValues from a CSD record (or API response). Standalone
// so the form can reset in place after a recalculation without re-mounting.
export const buildFormValues = (rec) => {
  const record = rec || {};
  const monthlies = (record.buildings || []).flatMap((building) => {
    const saved = (building.months || []).filter((m) => m.id);
    return saved.length ? saved.map((m) => toRow(building, m)) : [toRow(building)];
  });

  return {
    percent_change_of_release: record.percent_change_of_release ?? 100,
    remark: record.remark || '',
    monthlies,
  };
};

// A new split row for the same building: no date (hence no month) until CSD picks one.
export const emptySplitRow = (row) => toRow({ building_id: row.building_id, building_name: row.building_name });
